import React from "react";
import { useEffect, useState } from "react";
import { FilterDropdowns } from "../components/FilterDropdowns";
import { supabase } from "../supabase/client";
import QuotaDisplay from "../components/billing/QuotaDisplay";
import CostPreviewModal from "../components/billing/CostPreviewModal";
import { PromoCodeValidationResponse, TierCounts } from "../types/billing";
// Header is provided by AuthenticatedRoute wrapper

const LeadGenerator: React.FC = () => {
  // getFilteredLeads endpoint
  const filteredLeadsEndpoint = "https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/getFilteredLeads";

  // Lead results state
  interface Lead {
    norm_country: string;
    norm_city: string;
    norm_job_level: string | null;
    norm_job_function: string | null;
    h_Lead_Score: string;
  }
  // CI/CD test: harmless comment added to trigger workflow
  const [leads, setLeads] = useState<Lead[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leadsError, setLeadsError] = useState<string | null>(null);

  // Preview leads handler
  const handlePreviewLeads = async () => {
    setLoadingLeads(true);
    setLeadsError(null);
    try {
      // Only send non-empty filters
      const filters = Object.fromEntries(
        Object.entries(selectedFilters).filter(([, v]) => v)
      );
      const data = await import('../utils/fetchFilteredLeads').then(mod =>
        mod.fetchFilteredLeads({
          endpointUrl: filteredLeadsEndpoint,
          jwt,
          filters
        })
      );
      // Accept either {sampleLeads: [...]}, {leads: [...]}, or [...]
      setLeads(data.sampleLeads || data.leads || data);
    } catch (err) {
      setLeadsError((err as Error).message);
      setLeads([]);
    } finally {
      setLoadingLeads(false);
    }
  };
  // Supabase client is imported as a singleton from ../supabase/client

  // JWT state
  const [jwt, setJwt] = useState<string>("");
  const [loadingJwt, setLoadingJwt] = useState(true);
  const [jwtError, setJwtError] = useState<string | null>(null);
  const filterMetadataEndpoint = "https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/getFilterMetadata";

  // Dropdown selections state
  const [selectedFilters, setSelectedFilters] = useState({
    norm_country: "",
    norm_city: "",
    norm_job_level: "",
    norm_job_function: ""
  });

  // Add export state
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportLeadsEndpoint = "https://zeeovxbtdyxncikfonij.supabase.co/functions/v1/exportLeads";

  // Cost preview modal state
  const [showCostPreview, setShowCostPreview] = useState(false);
  const [costPreviewData, setCostPreviewData] = useState<{
    leadCount: number;
    tierBreakdown: TierCounts;
    filters: Record<string, string | string[]>;
  } | null>(null);

  // Export handler - now shows cost preview first
  const handleExportLeads = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const filters = Object.fromEntries(
        Object.entries(selectedFilters).filter(([, v]) => v)
      );
      const res = await fetch(exportLeadsEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${jwt}`
        },
        body: JSON.stringify({ filters })
      });
      
      if (res.status === 402) {
        // Payment required - show cost preview modal
        const paymentData = await res.json();
        
        // Extract tier breakdown from pricing data
        const tierBreakdown: TierCounts = paymentData.pricing?.tierCounts || {};
        
        setCostPreviewData({
          leadCount: paymentData.leadsCount || 0,
          tierBreakdown,
          filters
        });
        setShowCostPreview(true);
        return;
      }
      
      if (!res.ok) {
        const err = await res.text();
        throw new Error(err);
      }
      
      // CSV download
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setExportError((err instanceof Error ? err.message : 'Export failed'));
    } finally {
      setExporting(false);
    }
  };

  // Handle cost preview confirmation
  const handleCostPreviewConfirm = async (finalCost: number, promoCode?: PromoCodeValidationResponse) => {
    setShowCostPreview(false);
    
    // Create Stripe checkout with or without promo code
    try {
      const filters = Object.fromEntries(
        Object.entries(selectedFilters).filter(([, v]) => v)
      );
      
      const checkoutData: {
        filters: Record<string, string | string[]>;
        createCheckout: boolean;
        promoCode?: string;
        stripePromotionCodeId?: string;
      } = { 
        filters,
        createCheckout: true  // Flag to indicate we want Stripe checkout, not just pricing
      };
      if (promoCode && promoCode.isValid) {
        checkoutData.promoCode = promoCode.code;
        checkoutData.stripePromotionCodeId = promoCode.stripePromotionCodeId;
      }
      
      const res = await fetch(exportLeadsEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${jwt}`
        },
        body: JSON.stringify(checkoutData)
      });
      
      if (res.status === 402) {
        const { stripeCheckoutUrl } = await res.json();
        window.location.href = stripeCheckoutUrl;
      } else {
        throw new Error('Unexpected response from export endpoint');
      }
    } catch (err) {
      setExportError((err instanceof Error ? err.message : 'Failed to create checkout'));
    }
  };

  const handleCostPreviewCancel = () => {
    setShowCostPreview(false);
    setCostPreviewData(null);
  };

  // Back button handler
  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/'; // fallback to home
    }
  };

  useEffect(() => {
    setLoadingJwt(true);
    supabase.auth.getSession().then(({ data, error }) => {
      if (error || !data.session) {
        setJwtError("Could not get Supabase session JWT. Please log in.");
        setLoadingJwt(false);
        return;
      }
      setJwt(data.session.access_token);
      setLoadingJwt(false);
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="flex flex-col items-center justify-center py-8">
        <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-4xl mx-4">
          {/* Page Header */}
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-purple-700">Encore Lead Generator</h1>
            <p className="text-lg text-gray-700 mt-2">
              Advanced BigQuery-powered filter dropdowns (Epoch 12)
            </p>
          </div>
        
        {/* Quota Display */}
        <div className="mb-6">
          <QuotaDisplay variant="page" showUpgradePrompt={true} />
        </div>
        {loadingJwt ? (
          <div>Loading authentication...</div>
        ) : jwtError ? (
          <div style={{ color: 'red' }}>{jwtError}</div>
        ) : (
          <>
            <FilterDropdowns
              endpointUrl={filterMetadataEndpoint}
              jwt={jwt}
              selected={selectedFilters}
              setSelected={setSelectedFilters}
            />
            <div className="mt-6">
              <h2 className="text-lg font-semibold mb-2">Selected Filters:</h2>
              <ul className="list-disc ml-6">
                <li>Country: {selectedFilters.norm_country || <span className="text-gray-400">(none)</span>}</li>
                <li>City: {selectedFilters.norm_city || <span className="text-gray-400">(none)</span>}</li>
                <li>Job Level: {selectedFilters.norm_job_level || <span className="text-gray-400">(none)</span>}</li>
                <li>Job Function: {selectedFilters.norm_job_function || <span className="text-gray-400">(none)</span>}</li>
              </ul>
              <button
                className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                onClick={handlePreviewLeads}
                disabled={loadingLeads || !Object.values(selectedFilters).some(v => v)}
              >
                {loadingLeads ? 'Loading Leads...' : 'Preview Leads'}
              </button>
              {leadsError && <div className="mt-2 text-red-600">Error: {leadsError}</div>}
              {leads.length > 0 && (
                <div className="mt-6 overflow-x-auto">
                  <h2 className="text-lg font-semibold mb-2">Lead Results:</h2>
                  <table className="min-w-full border">
                    <thead>
                      <tr>
                        <th className="px-2 py-1 border-b bg-gray-100 text-xs font-bold">Country</th>
                        <th className="px-2 py-1 border-b bg-gray-100 text-xs font-bold">City</th>
                        <th className="px-2 py-1 border-b bg-gray-100 text-xs font-bold">Job Level</th>
                        <th className="px-2 py-1 border-b bg-gray-100 text-xs font-bold">Job Function</th>
                        <th className="px-2 py-1 border-b bg-gray-100 text-xs font-bold">Lead Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.slice(((currentPage || 1) - 1) * 10, (currentPage || 1) * 10).map((lead, i) => (
                        <tr key={i} className="border-b">
                          <td className="px-2 py-1 border-r text-sm">{lead.norm_country}</td>
                          <td className="px-2 py-1 border-r text-sm">{lead.norm_city}</td>
                          <td className="px-2 py-1 border-r text-sm">{lead.norm_job_level}</td>
                          <td className="px-2 py-1 border-r text-sm">{lead.norm_job_function}</td>
                          <td className="px-2 py-1 border-r text-sm">{lead.h_Lead_Score}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Pagination Controls */}
                  <div className="flex justify-center items-center mt-4 gap-2">
                    <button
                      className="px-3 py-1 border rounded bg-gray-100 hover:bg-gray-200"
                      onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="px-2">Page {currentPage || 1} of {Math.ceil(leads.length / 10)}</span>
                    <button
                      className="px-3 py-1 border rounded bg-gray-100 hover:bg-gray-200"
                      onClick={() => setCurrentPage((p: number) => Math.min(Math.ceil(leads.length / 10), p + 1))}
                      disabled={currentPage === Math.ceil(leads.length / 10)}
                    >
                      Next
                    </button>
                  </div>
                  {/* Export and Back Buttons */}
                  <div className="flex flex-col items-center mt-8">
                    <button
                      className="px-6 py-2 bg-green-600 text-white rounded hover:bg-green-700 mb-2"
                      onClick={handleExportLeads}
                      disabled={exporting}
                    >
                      {exporting ? 'Exporting...' : 'Export Leads to CSV'}
                    </button>
                    {exportError && <div className="text-red-600 mt-2">{exportError}</div>}
                    <button
                      className="px-6 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 mt-4"
                      onClick={handleBack}
                    >
                      Back
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
        
        {/* Cost Preview Modal */}
        {costPreviewData && (
          <CostPreviewModal
            isOpen={showCostPreview}
            leadCount={costPreviewData.leadCount}
            tierBreakdown={costPreviewData.tierBreakdown}
            filters={costPreviewData.filters}
            onConfirm={handleCostPreviewConfirm}
            onCancel={handleCostPreviewCancel}
          />
        )}
        </div>
      </div>
    </div>
  );
};

export default LeadGenerator;
